// Global packages and components
import slugify from 'slugify'
import { useEffect, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useWindowDimensions } from '~/hooks'

// Types
import type { ProductDetailsProps } from '~/types'

interface Props {
  options: ProductDetailsProps['options']
  selected: string
  onChange: (e: string) => void
  clearProductData: () => void
}

// Decides which component to use
const shouldShowSelect = (values: string[]) => {
  return values.length > 5 ? true : false
}

// Main export
const VariantOptions = ({
  options,
  selected,
  onChange,
  clearProductData,
}: Props) => {
  const { width } = useWindowDimensions()
  const optionSlug = slugify(options.name.toLowerCase())
  const [showSelect, setShowSelect] = useState<boolean>(
    shouldShowSelect(options.values)
  )

  // If we're on a small size, change the default display
  useEffect(() => {
    if ((width && width < 1024) || shouldShowSelect(options.values)) {
      setShowSelect(true)
    } else {
      setShowSelect(false)
    }
  }, [width, options.values])

  return (
    <div className="mb-6">
      {showSelect ? (
        <>
          <label
            htmlFor={optionSlug}
            className="mb-2 block text-sm"
          >
            Select {options.name}
          </label>
          <div className="relative md:max-w-[50%]">
            <select
              id={optionSlug}
              onChange={e => {
                onChange(e.target.value)
                clearProductData()
              }}
              value={selected}
              className="block w-full appearance-none rounded-lg border border-neutral-300 bg-white py-3 pl-4 pr-12 focus:border-teal-green focus:outline-none"
            >
              {options.values.map((e, i) => {
                const valueSlug = slugify(e).toLowerCase()

                return (
                  <option
                    key={i}
                    value={valueSlug}
                  >
                    {e}
                  </option>
                )
              })}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-4 top-1/2 size-5 -translate-y-1/2" />
          </div>
        </>
      ) : (
        <>
          <div className="mb-2 block text-sm">Select {options.name}</div>
          <div className="flex flex-wrap gap-2">
            {options.values.sort().map((e, i) => {
              const valueSlug = slugify(e.toLowerCase())

              return (
                <div
                  className="relative"
                  key={i}
                >
                  <input
                    key={i}
                    type="radio"
                    name={optionSlug}
                    id={`${optionSlug}-${i}`}
                    value={valueSlug}
                    checked={selected === valueSlug}
                    onChange={() => {
                      onChange(valueSlug)
                      clearProductData()
                    }}
                    className="peer absolute size-5 shrink-0 appearance-none"
                  />
                  <div className="pointer-events-none absolute left-3 top-1/2 size-3 -translate-y-1/2 rounded-full border border-neutral-300 ring-1 ring-transparent ring-offset-2 peer-checked:bg-teal-green peer-checked:ring-teal-green"></div>
                  <label
                    htmlFor={`${optionSlug}-${i}`}
                    className="inline-block w-full cursor-pointer rounded-lg border border-neutral-300 bg-white py-2 pl-10 pr-2 peer-checked:border-teal-green"
                  >
                    {e}
                  </label>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

export default VariantOptions
